import React, { useEffect, useState, useRef, useReducer } from "react";
import { useParams, useLocation } from "react-router-dom";
import { fetchBoats } from "../data/boats.js";
import { fetchSpacecraftInfo, fetchSpacecraftFamilies } from "../data/spacecraft.js";
import { fetchStageInfo } from "../data/stages.js";
import { fetchZoneInfo, fetchZones } from "../data/recoveryZones.js";
import { fetchRockets } from "../data/rocketFamilies.js";
import { fetchPads, fetchPadInfo } from "../data/pads.js";
import Card from "../informationCards/InformationCard.js";
import SwitchSeriesType from "../graphComponents/DynamicChart.js";
import CustomLegend from "../multipurpose/CustomLegend";
import dayjs from "dayjs";
import { getPhotoLocation, toTitleCase } from "../utils";
import "./vehicleInfo.css";

const STARSHIP_FALLBACK_IMAGE = "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg";

const cleanImagePath = (image) => {
  if (!image) {
    return "";
  }

  return String(image).replace(/^http:\/\/127\.0\.0\.1:8000\/?/, "/");
};

const getImageUrl = (image) => {
  const imagePath = cleanImagePath(image);

  if (!imagePath) {
    return "";
  }

  if (/^https?:\/\//.test(imagePath)) {
    return imagePath;
  }

  return `${getPhotoLocation()}${imagePath.startsWith("/") ? imagePath : `/${imagePath}`}`;
};

const isGenericStageImage = (image) => /\/media\/stage_photos\/default_booster\.jpg$/i.test(cleanImagePath(image));

const getVehicleImageUrl = (vehicle, rockets) => {
  const rocket = rockets.find((item) => item.id === vehicle?.rocket);
  const shouldUseRocketImage = rocket?.name === "Starship" && (!vehicle?.image || isGenericStageImage(vehicle.image));

  if (shouldUseRocketImage) {
    return getImageUrl(STARSHIP_FALLBACK_IMAGE);
  }

  return getImageUrl(vehicle?.image) || getImageUrl(rocket?.image);
};

const formatEnum = (value, fallback = "Unknown") =>
  toTitleCase(String(value || fallback).replace(/_/g, " ")).replace("Tbd", "TBD");

const formatDate = (value) => {
  if (!value) {
    return "TBD";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "TBD";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date);
};

const formatDays = (days) => {
  if (!Number.isFinite(Number(days))) {
    return "TBD";
  }

  const rounded = Math.round(Number(days) * 10) / 10;
  return `${rounded}d`;
};

const getVehicleTypeLabel = (vehicle, url) => {
  if (url.includes("spacecraft")) {
    return "Spacecraft";
  }

  if (url.includes("pad")) {
    return "Launch pad";
  }

  if (url.includes("zone")) {
    return "Recovery zone";
  }

  return formatEnum(vehicle?.type, "Stage");
};

// calculateStats takes in a list of turnarounds and yields stats
const calculateStats = (data, count) => {
  if (!data.length) {
    return {
      Fastest: "TBD",
      Average: "TBD",
      "Standard Deviation": "TBD",
      Flights: count,
    };
  }

  const reuseCount = data.length;
  const fastest = Math.min(...data);
  const average = Math.round((data.reduce((a, b) => a + b, 0) / reuseCount) * 100) / 100;
  const stdev = Math.round(Math.sqrt(data.reduce((a, b) => a + Math.pow(b - average, 2), 0) / reuseCount) * 100) / 100;

  return {
    Fastest: `${fastest} days`,
    Average: `${average} days`,
    "Standard Deviation": `${stdev} days`,
    Flights: count,
  };
};

const VehicleDetails = () => {
  const { id } = useParams();
  const url = useLocation().pathname;
  const [vehicle, setVehicle] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [chosenDate, setChosenDate] = useState(null);
  const [prevItemsForDisplay, setPrevItemsForDisplay] = useState([]);
  const [rockets, setRockets] = useState([]);
  const [boats, setBoats] = useState([]);
  const [zones, setZones] = useState([]);
  const [pads, setPads] = useState([]);
  const [families, setFamilies] = useState([]);
  const [objectType, setObjectType] = useState("");
  const [turnarounds, setTurnarounds] = useState([]);
  const [showGraph, setShowGraph] = useState(null);
  const [xAxis, setxAxis] = useState([]);
  const [futureLaunches, setFutureLaunches] = useState([]);
  const [launchNames, setLaunchNames] = useState([]);
  const [turnaroundStats, setTurnaroundStats] = useState([]);
  const [, forceUpdate] = useReducer((x) => x + 1, 0); // Graph does not update on page resize; force update with this
  const [error, setError] = useState(null); // State to handle errors

  const chartRef = useRef(null); // Ref to access the chart container
  const [dataLoaded, setDataLoaded] = useState(false); // Ref to signify when data has been loaded

  // Reusable function to handle error
  async function fetchWithErrorHandling(apiFunction, ...args) {
    try {
      return await apiFunction(...args);
    } catch (error2) {
      setError(error2.message);
      return null;
    }
  }

  useEffect(() => {
    (async () => {
      const [boatData, zoneData, rocketData, padData] = await Promise.all([
        fetchBoats(),
        fetchZones("", {}),
        fetchRockets(),
        fetchPads("", {}),
      ]);

      setBoats(boatData);
      setZones(zoneData);
      setRockets(rocketData);
      setPads(padData);
      setDataLoaded(true);
    })();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!dataLoaded) {
        return;
      }

      let reuseData = [];
      let launchData = [];
      let prevDisplayItems = [];
      let launchNameData = [];
      let turnaroundData = [];
      let xAxisData = [];
      let key = "";
      let prevLaunches = [];
      let futureLaunches = [];
      let vehicleData = {};

      if (url.includes("spacecraft")) {
        vehicleData = await fetchWithErrorHandling(fetchSpacecraftInfo, id, chosenDate);
        if (vehicleData) {
          const familyData = await fetchWithErrorHandling(fetchSpacecraftFamilies);
          reuseData = vehicleData.spacecraft_on_launches;
          launchData = reuseData.map((reuse) => reuse.launch);
          prevDisplayItems = vehicleData.display_spacecraft_on_launches;
          setVehicle(vehicleData["spacecraft"]);
          setObjectType("spacecraftInfo");
          setFamilies(familyData);
          key = "spacecraft";
        }
      } else if (url.includes("stage")) {
        vehicleData = await fetchWithErrorHandling(fetchStageInfo, id, chosenDate);
        if (vehicleData) {
          reuseData = vehicleData.stage_and_recoveries;
          launchData = reuseData.map((reuse) => reuse.launch);
          prevDisplayItems = vehicleData.display_stage_and_recoveries;
          setVehicle(vehicleData["stage"]);
          setObjectType("stageInfo");
          key = "stage";
        }
      } else if (url.includes("zone")) {
        vehicleData = await fetchWithErrorHandling(fetchZoneInfo, id, chosenDate);
        if (vehicleData) {
          reuseData = vehicleData.stage_and_recoveries;
          launchData = reuseData.map((reuse) => reuse.launch);
          prevDisplayItems = vehicleData.display_stage_and_recoveries;
          setVehicle(vehicleData["landing_zone"]);
          setObjectType("zoneInfo");
          key = "zone";
        }
      } else if (url.includes("pad")) {
        vehicleData = await fetchWithErrorHandling(fetchPadInfo, id, chosenDate);
        if (vehicleData) {
          launchData = vehicleData.launches;
          prevDisplayItems = vehicleData.display_launches;
          setVehicle(vehicleData.pad);
          setObjectType("padInfo");
          key = "pad";
        }
      }

      if (!vehicleData) {
        return;
      }

      setStartDate(new Date(vehicleData["start_date"].slice(0, -1)));
      if (chosenDate === null) {
        setChosenDate(new Date(vehicleData["start_date"].slice(0, -1)));
      }

      const currentDate = new Date();
      if (url.includes("stage") || url.includes("spacecraft") || url.includes("zone")) {
        for (const reuse of reuseData) {
          if (reuse[`${key}_turnaround`]) {
            turnaroundData.push(Math.round((reuse[`${key}_turnaround`] / 86400) * 100) / 100);
            launchNameData.push(reuse.launch.name);
          }
        }
        prevLaunches = reuseData.filter((reuse) => new Date(reuse.launch.time) <= currentDate);
        futureLaunches = reuseData.filter((reuse) => new Date(reuse.launch.time) > currentDate);
      } else {
        for (const launch of launchData) {
          if (launch[`${key}_turnaround`]) {
            turnaroundData.push(Math.round((launch[`${key}_turnaround`] / 86400) * 100) / 100);
            launchNameData.push(launch.name);
          }
        }
        prevLaunches = launchData.filter((launch) => new Date(launch.time) <= currentDate);
        futureLaunches = launchData.filter((launch) => new Date(launch.time) > currentDate);
      }

      for (let i = 0; i < turnaroundData.length; i++) {
        xAxisData.push(i);
      }

      if (showGraph === null) {
        if (turnaroundData.length >= 2) {
          setShowGraph(true);
        } else {
          setShowGraph(false);
        }
      }
      setLaunchNames(launchNameData);
      setPrevItemsForDisplay(prevDisplayItems);
      setFutureLaunches(futureLaunches);
      setTurnarounds(turnaroundData);
      setTurnaroundStats(calculateStats(turnaroundData.slice(0, prevLaunches.length), prevLaunches.length));
      setxAxis(xAxisData);
    };
    fetchData();
  }, [id, url, chosenDate, showGraph, dataLoaded]);

  useEffect(() => {
    const handleResize = () => {
      forceUpdate();
    };
    handleResize();
    // Add resize event listener
    window.addEventListener("resize", handleResize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [dataLoaded, vehicle]);

  // check to make sure vehicle exists
  if (error) {
    return <div style={{ textAlign: "center", color: "white" }}>{error}</div>;
  }

  // have loading indicator
  if (!vehicle) {
    return <div className="loading-indicator"></div>;
  }

  const handleDateChange = (newDate) => {
    const date = dayjs(newDate);
    if (date.isValid()) {
      setChosenDate(date.startOf("day").toDate());
    }
  };

  // Generate the header text based on the vehicle type
  const generateHeaderText = () => {
    if (url.includes("stage")) {
      return `${vehicle.name}${vehicle.nickname ? ` ${vehicle.nickname}` : ""} | ${
        rockets.find((rocket) => vehicle.rocket === rocket.id)?.name
      } ${vehicle.version}`;
    } else if (url.includes("spacecraft")) {
      return `${vehicle.name}${vehicle.nickname ? ` ${vehicle.nickname}` : ""} | ${
        families.find((family) => vehicle.family === family.id)?.name
      } ${vehicle.version}`;
    } else if (url.includes("pad")) {
      return `${vehicle.name} | ${vehicle.nickname}`;
    } else if (url.includes("zone")) {
      return `${vehicle.name} | ${vehicle.nickname}`;
    }
  };

  const vehicleImageUrl = getVehicleImageUrl(vehicle, rockets);
  const rocketName = url.includes("stage") ? rockets.find((rocket) => vehicle.rocket === rocket.id)?.name : "";
  const spacecraftFamilyName = url.includes("spacecraft")
    ? families.find((family) => vehicle.family === family.id)?.name
    : "";
  const objectFamily = rocketName || spacecraftFamilyName || vehicle.nickname || "Fleet object";
  const vehicleStatus = formatEnum(vehicle.status, "Unknown");
  const vehicleTypeLabel = getVehicleTypeLabel(vehicle, url);
  const historyItems = [...(futureLaunches || []), ...(prevItemsForDisplay || [])];
  const historyDates = historyItems
    .map((item) => item.launch?.time || item.time)
    .filter(Boolean)
    .map((time) => new Date(time))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a - b);
  const firstFlight = historyDates[0] ? formatDate(historyDates[0].toISOString()) : "TBD";
  const latestFlight = historyDates[historyDates.length - 1] ? formatDate(historyDates[historyDates.length - 1].toISOString()) : "TBD";
  const validTurnarounds = turnarounds.filter((turnaround) => Number.isFinite(Number(turnaround)));
  const fastestTurnaround = validTurnarounds.length ? formatDays(Math.min(...validTurnarounds)) : "TBD";
  const latestTurnaround = validTurnarounds.length ? formatDays(validTurnarounds[validTurnarounds.length - 1]) : "TBD";
  const profileFacts = [
    ["Object", vehicleTypeLabel],
    ["Program", objectFamily],
    ["Version", vehicle.version || "Unknown"],
    ["Status", vehicleStatus],
  ];
  const metrics = [
    ["Flights", vehicle.num_launches ?? historyItems.length ?? 0],
    ["Previous", prevItemsForDisplay?.length || 0],
    ["Upcoming", futureLaunches?.length || 0],
    ["Fastest turn", fastestTurnaround],
  ];

  return (
    <div className="vd-page">
      <section className="vd-hero">
        <div className="vd-hero-copy">
          <p className="bt-section-label">{objectFamily} {vehicleTypeLabel}</p>
          <h1>{vehicle.name}</h1>
          <p>{vehicle.version ? `${vehicle.version} hardware profile` : "Hardware profile"}</p>
          <div className="vd-status-row">
            <span className={`vd-status-pill ${String(vehicle.status || "").toLowerCase()}`}>{vehicleStatus}</span>
            <span>{generateHeaderText()}</span>
          </div>
        </div>
        <div className="vd-hero-image">
          {vehicleImageUrl ? <img src={vehicleImageUrl} alt={generateHeaderText()} /> : null}
        </div>
      </section>

      <section className="vd-metric-strip">
        {metrics.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </section>

      <section className="vd-dashboard">
        <div className="vd-main-column">
          {showGraph ? (
            <section className="vd-panel vd-chart-panel">
              <div className="vd-panel-head">
                <div>
                  <p className="bt-section-label">Turnaround profile</p>
                  <h2>Time between flights</h2>
                </div>
                <div className="vd-panel-stats">
                  <span>
                    <small>Latest</small>
                    <strong>{latestTurnaround}</strong>
                  </span>
                  <span>
                    <small>Fastest</small>
                    <strong>{fastestTurnaround}</strong>
                  </span>
                </div>
              </div>
              <div className="vd-chart-shell">
                <div className="vd-chart-frame" ref={chartRef}>
                  <SwitchSeriesType
                    seriesInput={[
                      {
                        data: turnarounds,
                        label: "Turnaround (days)",
                        area: false,
                        stack: "total",
                        curve: "linear",
                      },
                    ]}
                    xAxisInput={[{ data: xAxis, type: "linear", scaleType: "band" }]}
                    xAxisLabels={launchNames}
                    showxAxis={false}
                    showAdditionalInfo={true}
                    futureLaunchCount={futureLaunches.length}
                    displayStats={turnaroundStats}
                    legend={
                      <CustomLegend
                        legendItems={[
                          { color: "#238bb8", label: "Previous" },
                          { color: "#168f7d", label: "Future" },
                        ]}
                      />
                    }
                    chartWidth={chartRef.current?.clientWidth || 760}
                    widthOffset={0}
                    onDateChange={handleDateChange}
                    selectedDate={chosenDate}
                    startDate={startDate}
                  />
                </div>
              </div>
            </section>
          ) : (
            <section className="vd-panel vd-profile-panel">
              <div className="vd-panel-head">
                <div>
                  <p className="bt-section-label">Hardware profile</p>
                  <h2>{vehicle.name} summary</h2>
                </div>
              </div>
              <div className="vd-profile-grid">
                {profileFacts.map(([label, value]) => (
                  <span key={label}>
                    <small>{label}</small>
                    <strong>{value}</strong>
                  </span>
                ))}
              </div>
            </section>
          )}

          {futureLaunches?.length !== 0 ? (
            <section className="vd-panel vd-history-panel">
              <div className="vd-panel-head">
                <div>
                  <p className="bt-section-label">Manifest</p>
                  <h2>Future launches</h2>
                </div>
                <strong>{futureLaunches.length} scheduled</strong>
              </div>
              <div className="vehicle-launch-cards-container">
                {futureLaunches.map((launch) => (
                  <Card
                    key={launch.id}
                    object={{ ...launch, time: launch.time }}
                    objectType={objectType}
                    subObjects={{ boats: boats, zones: zones, rockets: rockets, pads: pads }}
                    additionalInfo={{}}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {prevItemsForDisplay?.length !== 0 ? (
            <section className="vd-panel vd-history-panel">
              <div className="vd-panel-head">
                <div>
                  <p className="bt-section-label">Mission history</p>
                  <h2>Previous launches</h2>
                </div>
                <strong>{prevItemsForDisplay.length} flown</strong>
              </div>
              <div className="vehicle-launch-cards-container">
                {prevItemsForDisplay.map((launch) => (
                  <Card
                    key={launch.id}
                    object={launch}
                    objectType={objectType}
                    subObjects={{ boats: boats, zones: zones, rockets: rockets, pads: pads }}
                    additionalInfo={{}}
                  />
                ))}
              </div>
            </section>
          ) : null}
        </div>

        <aside className="vd-side-column">
          <section className="vd-panel vd-record-panel">
            <p className="bt-section-label">Records</p>
            <dl>
              <div>
                <dt>First flight</dt>
                <dd>{firstFlight}</dd>
              </div>
              <div>
                <dt>Latest flight</dt>
                <dd>{latestFlight}</dd>
              </div>
              <div>
                <dt>Latest turn</dt>
                <dd>{latestTurnaround}</dd>
              </div>
            </dl>
          </section>
          <section className="vd-panel vd-object-panel">
            <p className="bt-section-label">Classification</p>
            <div className="vd-profile-grid compact">
              {profileFacts.map(([label, value]) => (
                <span key={label}>
                  <small>{label}</small>
                  <strong>{value}</strong>
                </span>
              ))}
            </div>
          </section>
        </aside>
      </section>
    </div>
  );
};

export default VehicleDetails;
